import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import typingGif from "../assets/gif/typing.gif";
import { VOCAB_CARDS } from "../data/vocabData";

// ─── Romaji → Hiragana ────────────────────────────────────────────────────────
const ROMAJI_MAP = {
    'sha':'しゃ','shi':'し','shu':'しゅ','she':'しぇ','sho':'しょ',
    'chi':'ち','cha':'ちゃ','chu':'ちゅ','che':'ちぇ','cho':'ちょ',
    'tsu':'つ',
    'kya':'きゃ','kyu':'きゅ','kyo':'きょ',
    'gya':'ぎゃ','gyu':'ぎゅ','gyo':'ぎょ',
    'nya':'にゃ','nyu':'にゅ','nyo':'にょ',
    'hya':'ひゃ','hyu':'ひゅ','hyo':'ひょ',
    'mya':'みゃ','myu':'みゅ','myo':'みょ',
    'rya':'りゃ','ryu':'りゅ','ryo':'りょ',
    'bya':'びゃ','byu':'びゅ','byo':'びょ',
    'pya':'ぴゃ','pyu':'ぴゅ','pyo':'ぴょ',
    'dya':'ぢゃ','dyu':'ぢゅ','dyo':'ぢょ',
    'sya':'しゃ','syu':'しゅ','syo':'しょ',
    'tya':'ちゃ','tyu':'ちゅ','tyo':'ちょ',
    'jya':'じゃ','jyu':'じゅ','jyo':'じょ',
    'ja':'じゃ','ji':'じ','ju':'じゅ','je':'じぇ','jo':'じょ',
    'nn':'ん',
    'a':'あ','i':'い','u':'う','e':'え','o':'お',
    'ka':'か','ki':'き','ku':'く','ke':'け','ko':'こ',
    'sa':'さ','si':'し','su':'す','se':'せ','so':'そ',
    'ta':'た','ti':'ち','tu':'つ','te':'て','to':'と',
    'na':'な','ni':'に','nu':'ぬ','ne':'ね','no':'の',
    'ha':'は','hi':'ひ','fu':'ふ','hu':'ふ','he':'へ','ho':'ほ',
    'ma':'ま','mi':'み','mu':'む','me':'め','mo':'も',
    'ya':'や','yu':'ゆ','yo':'よ',
    'ra':'ら','ri':'り','ru':'る','re':'れ','ro':'ろ',
    'wa':'わ','wo':'を',
    'ga':'が','gi':'ぎ','gu':'ぐ','ge':'げ','go':'ご',
    'za':'ざ','zi':'じ','zu':'ず','ze':'ぜ','zo':'ぞ',
    'da':'だ','di':'ぢ','du':'づ','de':'で','do':'ど',
    'ba':'ば','bi':'び','bu':'ぶ','be':'べ','bo':'ぼ',
    'pa':'ぱ','pi':'ぴ','pu':'ぷ','pe':'ぺ','po':'ぽ',
    'fa':'ふぁ','fi':'ふぃ','fe':'ふぇ','fo':'ふぉ',
    '-':'ー',
};

function romajiToHiragana(input) {
    const r = input.toLowerCase();
    let result = ''; let i = 0;
    while (i < r.length) {
        if (i + 1 < r.length && r[i] !== 'n' && r[i] === r[i+1] && /[bcdfghjklmpqrstvwxyz]/.test(r[i])) {
            result += 'っ'; i++; continue;
        }
        let matched = false;
        for (let len = 3; len >= 1; len--) {
            const seg = r.slice(i, i+len);
            if (len === 1 && seg === 'n') {
                const next = r[i+1];
                if (!next || /[bcdfghjklmpqrstvwxz]/.test(next)) { result += 'ん'; i++; matched = true; }
                break;
            }
            if (ROMAJI_MAP[seg]) { result += ROMAJI_MAP[seg]; i += len; matched = true; break; }
        }
        if (!matched) { result += r[i]; i++; }
    }
    return result;
}

function getHiraganaDisplay(romaji) {
    const r = romaji.toLowerCase();
    let converted = ''; let i = 0;
    while (i < r.length) {
        if (i + 1 < r.length && r[i] !== 'n' && r[i] === r[i+1] && /[bcdfghjklmpqrstvwxyz]/.test(r[i])) {
            converted += 'っ'; i++; continue;
        }
        let matched = false;
        for (let len = 3; len >= 1; len--) {
            const seg = r.slice(i, i+len);
            if (len === 1 && seg === 'n') {
                const next = r[i+1];
                if (!next || /[bcdfghjklmpqrstvwxz]/.test(next)) { converted += 'ん'; i++; matched = true; }
                break;
            }
            if (ROMAJI_MAP[seg]) { converted += ROMAJI_MAP[seg]; i += len; matched = true; break; }
        }
        if (!matched) return { converted, pending: r.slice(i) };
    }
    return { converted, pending: '' };
}

function checkHiragana(romaji) {
    return romajiToHiragana(romaji.toLowerCase().trim()).replace(/n$/, 'ん');
}

// ─── Typing romaji hint ────────────────────────────────────────────────────────
// Characters where the pronunciation (Hepburn) romaji differs from what you
// must actually type in this game (e.g. ぢ displays as "ji" but you type "di").
const TYPING_HINT_CHARS = new Set(['ぢ', 'づ']);

const H2R = {
    'きゃ':'kya','きゅ':'kyu','きょ':'kyo',
    'しゃ':'sha','しゅ':'shu','しょ':'sho',
    'ちゃ':'cha','ちゅ':'chu','ちょ':'cho',
    'にゃ':'nya','にゅ':'nyu','にょ':'nyo',
    'ひゃ':'hya','ひゅ':'hyu','ひょ':'hyo',
    'みゃ':'mya','みゅ':'myu','みょ':'myo',
    'りゃ':'rya','りゅ':'ryu','りょ':'ryo',
    'ぎゃ':'gya','ぎゅ':'gyu','ぎょ':'gyo',
    'じゃ':'ja','じゅ':'ju','じょ':'jo',
    'ぢゃ':'dya','ぢゅ':'dyu','ぢょ':'dyo',
    'びゃ':'bya','びゅ':'byu','びょ':'byo',
    'ぴゃ':'pya','ぴゅ':'pyu','ぴょ':'pyo',
    'あ':'a','い':'i','う':'u','え':'e','お':'o',
    'か':'ka','き':'ki','く':'ku','け':'ke','こ':'ko',
    'さ':'sa','し':'shi','す':'su','せ':'se','そ':'so',
    'た':'ta','ち':'chi','つ':'tsu','て':'te','と':'to',
    'な':'na','に':'ni','ぬ':'nu','ね':'ne','の':'no',
    'は':'ha','ひ':'hi','ふ':'fu','へ':'he','ほ':'ho',
    'ま':'ma','み':'mi','む':'mu','め':'me','も':'mo',
    'や':'ya','ゆ':'yu','よ':'yo',
    'ら':'ra','り':'ri','る':'ru','れ':'re','ろ':'ro',
    'わ':'wa','を':'wo','ん':'n',
    'が':'ga','ぎ':'gi','ぐ':'gu','げ':'ge','ご':'go',
    'ざ':'za','じ':'ji','ず':'zu','ぜ':'ze','ぞ':'zo',
    'だ':'da','ぢ':'di','づ':'du','で':'de','ど':'do',
    'ば':'ba','び':'bi','ぶ':'bu','べ':'be','ぼ':'bo',
    'ぱ':'pa','ぴ':'pi','ぷ':'pu','ぺ':'pe','ぽ':'po',
    'ー':'-',
};

function hiraganaToTypingRomaji(hira) {
    const chars = [...hira];
    let result = ''; let i = 0;
    while (i < chars.length) {
        if (chars[i] === 'っ') {
            const nxt = H2R[chars[i+1] + (chars[i+2] || '')] || H2R[chars[i+1]] || '';
            result += nxt[0] || 't';
            i++; continue;
        }
        if (i + 1 < chars.length) {
            const two = H2R[chars[i] + chars[i+1]];
            if (two) { result += two; i += 2; continue; }
        }
        result += H2R[chars[i]] || chars[i];
        i++;
    }
    return result;
}

// ─── Diff display component ───────────────────────────────────────────────────
// Thin blinking vertical cursor
function BlinkingCursor() {
    return (
        <motion.span
            className="inline-block bg-gray-800 rounded-sm"
            style={{ width: 2, height: "clamp(1.8rem,8vmin,3.2rem)" }}
            animate={{ opacity: [1, 1, 0, 0] }}
            transition={{ duration: 1, repeat: Infinity, ease: "linear", times: [0, 0.45, 0.5, 1] }}
        />
    );
}

// Shows what the user typed: correctly-typed prefix in light gray,
// a blank gap where a char was missing, wrong suffix in mid-gray.
function DiffDisplay({ correct, typed }) {
    const cArr = [...correct];
    const tArr = [...(typed || "")];

    if (!typed || typed.length === 0) {
        // Nothing typed (pass / timeout) — show blinking cursor
        return <BlinkingCursor />;
    }

    // Per-character coloring: gray if correct, red if wrong
    return (
        <span className="text-[clamp(2rem,9vmin,3.5rem)] font-bold tracking-widest leading-none font-jp">
            {tArr.map((c, i) => (
                <span key={i} className={c === cArr[i] ? "text-gray-300" : "text-red-500"}>{c}</span>
            ))}
        </span>
    );
}

// ─── Vocab lookup maps (built before audio loading so VOCAB_ITEMS can use them) ─
const VOCAB_BY_JP = {};
const VOCAB_BY_ROMAJI = {};
VOCAB_CARDS.forEach(card => {
    if (card.jp && !VOCAB_BY_JP[card.jp]) VOCAB_BY_JP[card.jp] = card;
    const r = (card.romaji || "").toLowerCase();
    if (r && !VOCAB_BY_ROMAJI[r]) VOCAB_BY_ROMAJI[r] = card;
    const k = (card.key || "").toLowerCase();
    if (k && !/[぀-ヿ]/.test(k) && !VOCAB_BY_ROMAJI[k]) VOCAB_BY_ROMAJI[k] = card;
});

// ─── Audio loading ─────────────────────────────────────────────────────────────
const VOCAB_ITEMS = [];
const GOODJOB_AUDIO = { en: null, zh: null, jp: null };
const FEEDBACK_SFX  = { correct: null, wrong: null };
let TAP_SFX     = null;
let TYPING_SFX  = null;  // played on Enter
let CLICK_SFX   = null;  // played on each key/kana tap
let RESULT_BGM        = null;
let TYPING_BGM        = null;
let VOCAB_BGM_FALLBACK = null;

try {
    const req = require.context("../assets/audio/vocab", false, /\.(mp3|wav|ogg|m4a)$/i);
    const seenH = new Set();
    req.keys().forEach((k) => {
        const m = k.match(/[/\\]([^/\\]+)\.\w+$/);
        if (!m) return;
        // Strip trailing digits and underscore-digits (e.g. ame1 → ame, ame_1 → ame)
        const rawKey = m[1].replace(/(_\d+|\d+)$/, '');
        const isJP = /[぀-ヿ]/.test(rawKey);
        const hiragana = isJP ? rawKey : romajiToHiragana(rawKey);
        // Look up authoritative JP from vocabData (fixes e.g. chidimu → ちぢむ not ちじむ)
        const vocabCard = VOCAB_BY_JP[hiragana] || VOCAB_BY_ROMAJI[rawKey.toLowerCase()];
        const authHiragana = vocabCard?.jp || hiragana;
        if (!authHiragana || seenH.has(authHiragana)) return; // deduplicate
        seenH.add(authHiragana);
        VOCAB_ITEMS.push({ key: rawKey, hiragana: authHiragana, url: req(k) });
    });
} catch (_) {}

if (VOCAB_ITEMS.length === 0) {
    try {
        const req = require.context("../assets/audio", false, /\.(mp3|m4a)$/i);
        const seen = {};
        req.keys().forEach((k) => {
            const m = k.match(/[/\\]([a-z]+)\.(m4a|mp3)$/i);
            if (!m) return;
            const key = m[1].toLowerCase();
            const hiragana = checkHiragana(key);
            if (/[a-z]/i.test(hiragana)) return;
            if (!seen[key]) {
                seen[key] = true;
                VOCAB_ITEMS.push({ key, hiragana, url: req(k) });
            }
        });
    } catch (_) {}
}

// ─── Vocab images ──────────────────────────────────────────────────────────────
const VOCAB_IMG = {};
try {
    const imgCtx = require.context("../assets/images/vocab", false, /\.(png|jpe?g|PNG|JPG|JPEG)$/i);
    imgCtx.keys().forEach((k) => {
        const name = k.replace(/^\.\//, "").replace(/\.(png|jpe?g|PNG|JPG|JPEG)$/i, "").normalize("NFC");
        VOCAB_IMG[name] = imgCtx(k);
    });
} catch (_) {}

function getVocabImg(key) {
    if (!key) return null;
    const k = key.normalize("NFC");
    return VOCAB_IMG[`${k}_1`]
        || VOCAB_IMG[`${k}1`]
        || VOCAB_IMG[`${k}１`]
        || VOCAB_IMG[`${k}_2`]
        || VOCAB_IMG[`${k}2`]
        || VOCAB_IMG[k]
        || null;
}

function getVocabImgFrames(key) {
    if (!key) return [];
    const k = key.normalize("NFC");
    const f1 = VOCAB_IMG[`${k}_1`] || VOCAB_IMG[`${k}1`] || VOCAB_IMG[`${k}１`] || VOCAB_IMG[k];
    const f2 = VOCAB_IMG[`${k}_2`] || VOCAB_IMG[`${k}2`] || VOCAB_IMG[`${k}２`];
    return [f1, f2].filter(Boolean);
}

function getFramesForResult(q, card) {
    const keys = [q?.key, q?.hiragana, card?.key, card?.romaji, card?.jp].filter(Boolean);
    for (const k of keys) {
        const frames = getVocabImgFrames(k);
        if (frames.length) return frames;
    }
    return [];
}

// ─── Hanamaru video ────────────────────────────────────────────────────────────
let HANAMARU_VIDEO = null;
try {
    const gifReq = require.context("../assets/gif", false, /\.(mp4|webm)$/i);
    gifReq.keys().forEach((k) => {
        if (/[/\\]hanamaru\.\w+$/i.test(k)) HANAMARU_VIDEO = gifReq(k);
    });
} catch (_) {}

function HanamaruCanvas({ videoSrc, play = true }) {
    const videoRef  = useRef(null);
    const canvasRef = useRef(null);
    const rafRef    = useRef(null);
    useEffect(() => {
        const video = videoRef.current, canvas = canvasRef.current;
        if (!video || !canvas) return;
        const ctx = canvas.getContext("2d", { willReadFrequently: true });
        const processFrame = () => {
            if (video.readyState >= 2 && !video.paused && !video.ended) {
                const w = canvas.width, h = canvas.height;
                ctx.drawImage(video, 0, 0, w, h);
                const img = ctx.getImageData(0, 0, w, h);
                const d = img.data;
                for (let i = 0; i < d.length; i += 4) {
                    const r = d[i], g = d[i+1], b = d[i+2];
                    if (g > 80 && g > r * 1.15 && g > b * 1.1) d[i+3] = 0;
                }
                ctx.putImageData(img, 0, 0);
            }
            rafRef.current = requestAnimationFrame(processFrame);
        };
        const onMeta = () => { if (video.videoWidth) { canvas.width = video.videoWidth; canvas.height = video.videoHeight; } };
        const onPlay = () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); rafRef.current = requestAnimationFrame(processFrame); };
        const onEnd  = () => { if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; } };
        video.addEventListener("loadedmetadata", onMeta);
        video.addEventListener("play", onPlay);
        video.addEventListener("ended", onEnd);
        return () => {
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
            video.removeEventListener("loadedmetadata", onMeta);
            video.removeEventListener("play", onPlay);
            video.removeEventListener("ended", onEnd);
            video.pause();
        };
    }, [videoSrc]);
    useEffect(() => {
        const v = videoRef.current;
        if (!v) return;
        if (play) v.play().catch(() => {}); else v.pause();
    }, [play]);
    return (
        <div className="pointer-events-none flex items-center justify-center">
            <video ref={videoRef} src={videoSrc} playsInline muted
                style={{ position: "absolute", width: 1, height: 1, opacity: 0, pointerEvents: "none" }} />
            <canvas ref={canvasRef} width={480} height={480}
                style={{ maxWidth: "min(320px, 85vw)", height: "auto", display: "block" }} />
        </div>
    );
}

function AnimatedVocabImg({ frames, alt, className }) {
    const [idx, setIdx] = useState(0);
    useEffect(() => {
        if (frames.length <= 1) return;
        const id = setInterval(() => setIdx(i => (i + 1) % frames.length), 600);
        return () => clearInterval(id);
    }, [frames]);
    if (!frames.length) return null;
    return <img src={frames[idx]} alt={alt} className={className} />;
}

try {
    const req = require.context("../assets/audio", false, /\.(mp3|m4a|MP3|M4A)$/i);
    req.keys().forEach((k) => {
        const name = k.replace(/^\.\//, "").replace(/\..+$/, "").toLowerCase();
        if (name === "correct")        { FEEDBACK_SFX.correct = req(k); return; }
        if (name === "wrong")          { FEEDBACK_SFX.wrong   = req(k); return; }
        if (name === "tap")            { TAP_SFX              = req(k); return; }
        if (name === "typing")         { TYPING_SFX           = req(k); return; }
        if (name === "click")          { CLICK_SFX            = req(k); return; }
        if (name === "hiraganaquiz_bgm") { RESULT_BGM            = req(k); return; }
        if (name === "typing_bgm")    { TYPING_BGM            = req(k); return; }
        if (name === "vocabulary_bgm") { VOCAB_BGM_FALLBACK   = req(k); return; }
        const mGj = k.match(/[/\\]goodjob_(en|eng|zh|jp)\.\w+$/i);
        if (mGj) { const gl = mGj[1].toLowerCase(); GOODJOB_AUDIO[gl === "eng" ? "en" : gl] = req(k); return; }
        if (/[/\\]よくできました\.\w+$/i.test(k))  { GOODJOB_AUDIO.jp = req(k); return; }
        if (/[/\\]不错不错\.\w+$/i.test(k))         { GOODJOB_AUDIO.zh = req(k); return; }
        const mGj2 = k.match(/[/\\]good[\s_]?job[^/\\]*\.\w+$/i);
        if (mGj2 && !GOODJOB_AUDIO.en) { GOODJOB_AUDIO.en = req(k); }
    });
} catch (_) {}

function playSFX(src, volume = 1) {
    if (!src) return;
    try { const a = new Audio(src); a.volume = volume; a.play().catch(() => {}); } catch (_) {}
}

// ─── Translations ──────────────────────────────────────────────────────────────
const T = {
    en: {
        title:       'Type the word you hear in romaji!',
        titleMobile: 'Use the Japanese flick keyboard to enter the word you hear!',
        start:       'Start',
        desktop:     'Desktop',
        mobile:      'Mobile',
        enter:       'ENTER',
        pass:        'SKIP',
        tapToListen: 'tap to listen again',
        score:       'Score',
        outOf:       '/ 10',
        playAgain:   'Play Again',
        backToMenu:  'Back to menu',
        goodjob:     'Good job!',
        perfect:     'Perfect!',
        great:       'Great job!',
        keepGoing:   'Keep going!',
        tryAgain:    'Try again!',
        noAudio:     'No audio files found in audio/vocab/',
        missed:      'Review these',
        youTyped:    'You typed:',
        skipped:     'skipped',
        answer:      'Answer:',
        frontLabel:  'front',
        jpFront:     'Japanese',
        enFront:     'Eng & Chinese',
    },
    jp: {
        title:       'きこえたことばをローマじでうちこもう',
        titleMobile: 'きこえたことばをフリックでうちこもう！',
        start:       'はじめる',
        desktop:     'デスクトップ',
        mobile:      'モバイル',
        enter:       '決定',
        pass:        'スキップ',
        tapToListen: 'もういちどきく',
        score:       'スコア',
        outOf:       '/ 10',
        playAgain:   'もういっかい！',
        backToMenu:  'メニューにもどる',
        goodjob:     'よくできました！',
        perfect:     'ばっちり！',
        great:       'よくできました！',
        keepGoing:   'がんばって！',
        tryAgain:    'おしい！',
        noAudio:     'audio/vocab/ に音声ファイルがありません',
        missed:      'もう一度覚えよう',
        youTyped:    'あなたの答え：',
        skipped:     'スキップ',
        answer:      '正解：',
        frontLabel:  'をおもてにする',
        jpFront:     '日本語',
        enFront:     '英語・中国語',
    },
    zh: {
        title:       '把你听到的词用罗马字打出来吧！',
        titleMobile: '听发音，用日语九宫格键盘打出单词！',
        start:       '开始',
        desktop:     '电脑键盘',
        mobile:      '手机键盘',
        enter:       '确认',
        pass:        '跳过',
        tapToListen: '点击再听一遍',
        score:       '分数',
        outOf:       '/ 10',
        playAgain:   '再玩一次',
        backToMenu:  '返回菜单',
        goodjob:     '不错不错！',
        perfect:     '牛牛！',
        great:       '做得很好！',
        keepGoing:   '继续努力！',
        tryAgain:    '再试一次！',
        noAudio:     'audio/vocab/ 中没有音频文件',
        missed:      '再复习一遍',
        youTyped:    '你打的：',
        skipped:     '已跳过',
        answer:      '正解：',
        frontLabel:  '在前面',
        jpFront:     '日语',
        enFront:     '英语・中文',
    },
};

// ─── On-screen keyboards ──────────────────────────────────────────────────────
const KANA_BASE = [
    ['あ','い','う','え','お'],
    ['か','き','く','け','こ'],
    ['さ','し','す','せ','そ'],
    ['た','ち','つ','て','と'],
    ['な','に','ぬ','ね','の'],
    ['は','ひ','ふ','へ','ほ'],
    ['ま','み','む','め','も'],
    ['や','','ゆ','','よ'],
    ['ら','り','る','れ','ろ'],
    ['わ','を','ん','っ','ー'],
];
const KANA_VOICED = [
    ['が','ぎ','ぐ','げ','ご'],
    ['ざ','じ','ず','ぜ','ぞ'],
    ['だ','ぢ','づ','で','ど'],
    ['ば','び','ぶ','べ','ぼ'],
    ['ぱ','ぴ','ぷ','ぺ','ぽ'],
    ['ぁ','ぃ','ぅ','ぇ','ぉ'],
    ['ゃ','','ゅ','','ょ'],
];

// ─── 3-column Japanese phone-style keyboard ───────────────────────────────────
// Flick input: center=a-sound  up=u-sound  left=i-sound  right=e-sound  down=o-sound
// null = no kana in that direction / empty cell
const KANA3_BASE = [
    [{ key:'あ', c:'あ', u:'う', l:'い', r:'え', d:'お' }, { key:'か', c:'か', u:'く', l:'き', r:'け', d:'こ' }, { key:'さ', c:'さ', u:'す', l:'し', r:'せ', d:'そ' }],
    [{ key:'た', c:'た', u:'つ', l:'ち', r:'て', d:'と' }, { key:'な', c:'な', u:'ぬ', l:'に', r:'ね', d:'の' }, { key:'は', c:'は', u:'ふ', l:'ひ', r:'へ', d:'ほ' }],
    [{ key:'ま', c:'ま', u:'む', l:'み', r:'め', d:'も' }, { key:'や', c:'や', u:'ゆ', l:null, r:null, d:'よ' }, { key:'ら', c:'ら', u:'る', l:'り', r:'れ', d:'ろ' }],
];
const KANA3_VOICED = [
    [{ key:'が', c:'が', u:'ぐ', l:'ぎ', r:'げ', d:'ご' }, { key:'ざ', c:'ざ', u:'ず', l:'じ', r:'ぜ', d:'ぞ' }, { key:'だ', c:'だ', u:'づ', l:'ぢ', r:'で', d:'ど' }],
    [{ key:'ば', c:'ば', u:'ぶ', l:'び', r:'べ', d:'ぼ' }, { key:'ぱ', c:'ぱ', u:'ぷ', l:'ぴ', r:'ぺ', d:'ぽ' }, null],
    [{ key:'ぁ', c:'ぁ', u:'ぅ', l:'ぃ', r:'ぇ', d:'ぉ' }, { key:'ゃ', c:'ゃ', u:'ゅ', l:null, r:null, d:'ょ' }, null],
];
// Special fixed cells
const N_CELL     = { key:'ん', c:'ん', u:null, l:null, r:null, d:null };
const WA_CELL    = { key:'わ', c:'わ', u:'ん', l:'を', r:'ー', d:null };
const PUNCT_CELL = { key:'、', c:'、', u:'？', l:'。', r:'！', d:null };

// 小゛゜ modifier maps
const SMALL_MAP = {
    'や':'ゃ','ゃ':'や',
    'ゆ':'ゅ','ゅ':'ゆ',
    'よ':'ょ','ょ':'よ',
};
const DAKUTEN_MAP = {
    // か行: か→が→か
    'か':'が','き':'ぎ','く':'ぐ','け':'げ','こ':'ご',
    'が':'か','ぎ':'き','ぐ':'く','げ':'け','ご':'こ',
    // さ行: さ→ざ→さ
    'さ':'ざ','し':'じ','す':'ず','せ':'ぜ','そ':'ぞ',
    'ざ':'さ','じ':'し','ず':'す','ぜ':'せ','ぞ':'そ',
    // た行: つ→っ→づ→つ; others た→だ→た etc.
    'た':'だ','ち':'ぢ','つ':'っ','て':'で','と':'ど',
    'だ':'た','ぢ':'ち','っ':'づ','で':'て','ど':'と',
    'づ':'つ',
    // は行: は→ば→ぱ→は
    'は':'ば','ひ':'び','ふ':'ぶ','へ':'べ','ほ':'ぼ',
    'ば':'ぱ','び':'ぴ','ぶ':'ぷ','べ':'ぺ','ぼ':'ぽ',
    'ぱ':'は','ぴ':'ひ','ぷ':'ふ','ぺ':'へ','ぽ':'ほ',
};

function shuffle(arr) {
    const a = [...arr];
    for (let i = a.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
}

// ─── Countdown ring ────────────────────────────────────────────────────────────
function CountdownRing({ timeLeft, total }) {
    const animated = useRef(false);
    useEffect(() => { animated.current = true; });
    const pct    = total > 0 ? timeLeft / total : 1;
    const r      = 13;
    const circ   = 2 * Math.PI * r;
    const urgent = timeLeft <= 3;
    return (
        <div className="relative flex-shrink-0" style={{ width: 36, height: 36 }}>
            <svg width="36" height="36" viewBox="0 0 36 36" style={{ transform: "rotate(-90deg)" }}>
                <circle cx="18" cy="18" r={r} fill="none" stroke="#e5e7eb" strokeWidth="3" />
                <circle cx="18" cy="18" r={r} fill="none"
                    stroke={urgent ? "#ef4444" : "#2797c4"}
                    strokeWidth="3"
                    strokeDasharray={circ}
                    strokeDashoffset={circ * (1 - pct)}
                    style={{ transition: animated.current ? "stroke-dashoffset 0.92s linear, stroke 0.3s" : "none" }}
                />
            </svg>
            <span className={`absolute inset-0 flex items-center justify-center text-[11px] font-bold
                ${urgent ? "text-red-500" : "text-[#2797c4]"}`}>
                {timeLeft}
            </span>
        </div>
    );
}


const TOTAL_SECS = 10;
const IS_MOBILE_DEVICE =
    typeof navigator !== 'undefined' &&
    (/Android|iPhone|iPad|iPod|webOS|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
     (navigator.maxTouchPoints > 1 && 'ontouchstart' in window));

// ─── Main component ────────────────────────────────────────────────────────────
const TYPING_PAGE_META = {
    en: {
        title: "Flick or Type! | Hanapin – Japanese Typing Practice in Romaji",
        desc:  "Practice typing Japanese hiragana words in romaji. Fun flick input typing game for beginners! Flick or type to level up your Japanese listening and typing skills.",
    },
    jp: {
        title: "フリック！タイプ！ | はなぴん",
        desc:  "フリック入力またはローマ字で日本語の単語を入力して、タイピングスキルを上げよう！フリック入力の練習にも最適！",
    },
    zh: {
        title: "打字挑战！| 樱花老师",
        desc:  "用罗马字或滑动输入日语单词，练习打字，提升听力和打字水平！フリック入力",
    },
};

export default function Typing({ lang = "en", onPhaseChange }) {
    const t = T[lang] ?? T.en;

    const [phase,          setPhase]          = useState("start");
    const [kbMode,         setKbMode]         = useState(IS_MOBILE_DEVICE ? "mobile" : "desktop");
    const [kanaPage,       setKanaPage]       = useState("base");
    const [flickState,     setFlickState]     = useState(null);  // { cellKey, dir, rect }
    const flickTouchRef = useRef(null);                          // { startX, startY, cell }
    const [questions,      setQuestions]      = useState([]);
    const [qIdx,           setQIdx]           = useState(0);
    const [input,          setInput]          = useState("");
    const [timeLeft,       setTimeLeft]       = useState(null);
    const [feedback,       setFeedback]       = useState(null);
    const [wrongTyped,     setWrongTyped]     = useState("");   // what user typed on wrong
    const [score,          setScore]          = useState(0);
    const [results,        setResults]        = useState([]);
    const [resultRevealed, setResultRevealed] = useState(false);

    const [totalTime,       setTotalTime]       = useState(null);   // seconds for result

    // ── Reset flick state on new question ────────────────────────────────────
    useEffect(() => { setFlickState(null); flickTouchRef.current = null; }, [qIdx]);

    // ── visualViewport: track visible height so keyboard doesn't push content ─
    const [vpStyle, setVpStyle] = useState({});
    useEffect(() => {
        if (!IS_MOBILE_DEVICE || kbMode !== "desktop") {
            setVpStyle({});
            return;
        }
        const vv = window.visualViewport;
        if (!vv) return;
        const update = () => {
            setVpStyle({
                position: "fixed",
                top: `${vv.offsetTop}px`,
                left: `${vv.offsetLeft}px`,
                width: `${vv.width}px`,
                height: `${vv.height}px`,
            });
        };
        update();
        vv.addEventListener("resize", update);
        vv.addEventListener("scroll", update);
        return () => {
            vv.removeEventListener("resize", update);
            vv.removeEventListener("scroll", update);
            setVpStyle({});
        };
    }, [kbMode]);

    // ── Notify parent of phase changes (for footer hide) ─────────────────────
    useEffect(() => { onPhaseChange?.(phase); }, [phase, onPhaseChange]);

    // ── Body scroll lock: prevent iOS from scrolling page when keyboard opens ─
    // Applies whenever quiz is active on a mobile device (any keyboard mode).
    // position:fixed on the body is the most reliable iOS scroll-lock technique.
    useEffect(() => {
        if (phase !== "quiz" || !IS_MOBILE_DEVICE) return;
        const prevOverflow = document.body.style.overflow;
        const prevPosition = document.body.style.position;
        const prevWidth    = document.body.style.width;
        document.body.style.overflow = "hidden";
        document.body.style.position = "fixed";
        document.body.style.width    = "100%";
        return () => {
            document.body.style.overflow = prevOverflow;
            document.body.style.position = prevPosition;
            document.body.style.width    = prevWidth;
        };
    }, [phase]);

    // ── Per-page SEO ──────────────────────────────────────────────────────────
    useEffect(() => {
        const meta = TYPING_PAGE_META[lang] || TYPING_PAGE_META.en;
        document.title = meta.title;
        const el = document.querySelector('meta[name="description"]');
        if (el) el.setAttribute("content", meta.desc);
    }, [lang]);

    const inputRef        = useRef(null);
    const countdownRef    = useRef(null);
    const questionAudio   = useRef(null);   // pre-unlocked for iOS autoplay
    const resultBgmRef    = useRef(null);
    const typingBgmRef    = useRef(null);
    const resultDoneRef   = useRef(false);
    const answeredRef     = useRef(false);
    const scoreRef        = useRef(0);
    const retryTimeoutRef  = useRef(null);   // pending retry-clear timeout
    const quizStartTime    = useRef(null);   // wall-clock ms when quiz started
    const wrongAttemptsRef = useRef(new Map()); // hiragana → { q, typed } for ALL wrong tries

    const currentQ = questions[qIdx];
    const isLocked = feedback !== null;

    // ── audio helpers ─────────────────────────────────────────────────────────
    const playTap    = () => playSFX(TAP_SFX);
    const playClick  = () => playSFX(CLICK_SFX);
    const playTyping = () => playSFX(TYPING_SFX);

    const stopCountdown = () => {
        if (countdownRef.current) { clearInterval(countdownRef.current); countdownRef.current = null; }
    };

    // Pre-unlocked audio element for question playback (iOS fix)
    const playQuestion = (q) => {
        if (!q?.url) return;
        const pre = questionAudio.current;
        if (pre) {
            try { pre.pause(); } catch (_) {}
            pre.src = q.url;
            try { pre.load(); } catch (_) {}  // required after src change
            pre.volume = 1;
            pre.play().catch(() => {});
        } else {
            try { new Audio(q.url).play().catch(() => {}); } catch (_) {}
        }
    };

    // ── question load ─────────────────────────────────────────────────────────
    useEffect(() => {
        if (phase !== "quiz" || !currentQ) return;
        // Clear any pending retry from the previous question
        if (retryTimeoutRef.current) { clearTimeout(retryTimeoutRef.current); retryTimeoutRef.current = null; }
        answeredRef.current = false;
        setInput("");
        setFeedback(null);
        setWrongTyped("");
        stopCountdown();
        playQuestion(currentQ);

        let remaining = TOTAL_SECS;
        setTimeLeft(remaining);
        countdownRef.current = setInterval(() => {
            remaining--;
            setTimeLeft(remaining <= 0 ? 0 : remaining);
            if (remaining <= 0) stopCountdown();
        }, 1000);

        setTimeout(() => inputRef.current?.focus(), 80);
        return () => {
            stopCountdown();
            if (retryTimeoutRef.current) { clearTimeout(retryTimeoutRef.current); retryTimeoutRef.current = null; }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [qIdx, phase]);

    // ── timeout ───────────────────────────────────────────────────────────────
    useEffect(() => {
        if (timeLeft === 0 && phase === "quiz" && !answeredRef.current) handleTimeout();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [timeLeft]);

    // ── result BGM + goodjob ──────────────────────────────────────────────────
    useEffect(() => {
        if (phase !== "result") {
            resultBgmRef.current?.pause(); resultBgmRef.current = null;
            resultDoneRef.current = false;
            return;
        }
        if (resultDoneRef.current) return;
        resultDoneRef.current = true;

        // Start result BGM immediately when result screen shows
        if (RESULT_BGM && !resultBgmRef.current) {
            const bgm = new Audio(RESULT_BGM);
            bgm.loop = true; bgm.volume = 0.4;
            bgm.play().catch(() => {});
            resultBgmRef.current = bgm;
        }

        const doReveal = () => setResultRevealed(true);

        let t1, t2;
        const gjUrl = GOODJOB_AUDIO[lang] || GOODJOB_AUDIO.en;
        if (gjUrl) {
            const gj = new Audio(gjUrl);
            gj.onended = doReveal;
            gj.play().catch(() => { t1 = setTimeout(doReveal, 3000); });
            t2 = setTimeout(doReveal, 12000);
        } else {
            t1 = setTimeout(doReveal, 800);
        }
        return () => {
            clearTimeout(t1); clearTimeout(t2);
            resultBgmRef.current?.pause(); resultBgmRef.current = null;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [phase, lang]);

    // ── Typing BGM — play during quiz, stop on result/start ──────────────────
    useEffect(() => {
        const src = TYPING_BGM || VOCAB_BGM_FALLBACK;
        if (phase === "quiz") {
            if (src && !typingBgmRef.current) {
                const bgm = new Audio(src);
                bgm.loop = true; bgm.volume = 0.4;
                bgm.play().catch(() => {});
                typingBgmRef.current = bgm;
            }
        } else {
            typingBgmRef.current?.pause();
            typingBgmRef.current = null;
        }
    }, [phase]);

    // ── BGM unmount cleanup ───────────────────────────────────────────────────
    useEffect(() => {
        return () => {
            resultBgmRef.current?.pause();
            resultBgmRef.current = null;
            typingBgmRef.current?.pause();
            typingBgmRef.current = null;
        };
    }, []);

    // ── advance ───────────────────────────────────────────────────────────────
    const doAdvance = () => {
        if (retryTimeoutRef.current) { clearTimeout(retryTimeoutRef.current); retryTimeoutRef.current = null; }
        setFeedback(null);
        setWrongTyped("");
        setInput("");
        setKanaPage("base");
        if (qIdx + 1 >= 10) {
            stopCountdown();
            if (quizStartTime.current) {
                setTotalTime(Math.round((Date.now() - quizStartTime.current) / 1000));
            }
            setResultRevealed(false);
            setPhase("result");
        } else {
            setQIdx(i => i + 1);
        }
    };

    // ── submit ────────────────────────────────────────────────────────────────
    const handleSubmit = () => {
        if (!currentQ || answeredRef.current || isLocked) return;
        const typed = input.trim();
        if (!typed) return;

        const typedH  = kbMode === "mobile" ? typed : checkHiragana(typed);
        const correct = typedH === currentQ.hiragana;

        if (correct) {
            answeredRef.current = true;
            if (retryTimeoutRef.current) { clearTimeout(retryTimeoutRef.current); retryTimeoutRef.current = null; }
            stopCountdown();
            setWrongTyped("");
            setFeedback("correct");
            const newScore = scoreRef.current + 1;
            scoreRef.current = newScore;
            setScore(newScore);
            setResults(prev => [...prev, { correct: true, q: currentQ, typed: typedH }]);
            playSFX(FEEDBACK_SFX.correct, 0.3);
            setTimeout(doAdvance, 3000);
        } else {
            // One attempt only — finalise wrong immediately, show correct answer
            answeredRef.current = true;
            stopCountdown();
            setWrongTyped(typedH);
            setFeedback("wrong");
            playSFX(FEEDBACK_SFX.wrong, 0.3);
            const _wa = wrongAttemptsRef.current.get(currentQ.hiragana);
            wrongAttemptsRef.current.set(currentQ.hiragana, { q: currentQ, attempts: _wa ? [..._wa.attempts, typedH] : [typedH] });
            setResults(prev => [...prev, { correct: false, q: currentQ, typed: typedH }]);
            setTimeout(doAdvance, 1800);
        }
    };

    // ── pass ──────────────────────────────────────────────────────────────────
    const handlePass = () => {
        if (!currentQ || answeredRef.current || isLocked) return;
        if (retryTimeoutRef.current) { clearTimeout(retryTimeoutRef.current); retryTimeoutRef.current = null; }
        answeredRef.current = true;
        stopCountdown();
        setWrongTyped("");
        setFeedback("wrong");
        wrongAttemptsRef.current.set(currentQ.hiragana, { q: currentQ, attempts: [""] });
        setResults(prev => [...prev, { correct: false, q: currentQ, typed: "" }]);
        playSFX(FEEDBACK_SFX.wrong, 0.3);
        setTimeout(doAdvance, 1400);
    };

    // ── timeout ───────────────────────────────────────────────────────────────
    const handleTimeout = () => {
        if (answeredRef.current || !currentQ) return;
        if (retryTimeoutRef.current) { clearTimeout(retryTimeoutRef.current); retryTimeoutRef.current = null; }
        answeredRef.current = true;
        const typedH = kbMode === "mobile" ? input.trim() : checkHiragana(input.trim());
        setWrongTyped(typedH || "");
        setFeedback("wrong");
        const _wa4 = wrongAttemptsRef.current.get(currentQ.hiragana);
        wrongAttemptsRef.current.set(currentQ.hiragana, { q: currentQ, attempts: _wa4 ? [..._wa4.attempts, typedH || ""] : [typedH || ""] });
        setResults(prev => [...prev, { correct: false, q: currentQ, typed: typedH || "" }]);
        playSFX(FEEDBACK_SFX.wrong, 0.3);
        setTimeout(doAdvance, 1400);
    };

    // ── start ─────────────────────────────────────────────────────────────────
    const startQuiz = () => {
        playTap();
        // Unlock audio element during user gesture (iOS fix)
        if (!questionAudio.current) questionAudio.current = new Audio();
        const a = questionAudio.current;
        a.volume = 0;
        if (VOCAB_ITEMS[0]?.url) a.src = VOCAB_ITEMS[0].url;
        a.play().catch(() => {});

        const qs = shuffle(VOCAB_ITEMS).slice(0, 10);
        setQuestions(qs);
        setQIdx(0);
        setScore(0);
        scoreRef.current = 0;
        setResults([]);
        setInput("");
        setFeedback(null);
        setWrongTyped("");
        setTimeLeft(null);
        setTotalTime(null);
        setResultRevealed(false);
        answeredRef.current = false;
        wrongAttemptsRef.current = new Map();
        quizStartTime.current = Date.now();
        setPhase("quiz");
    };

    // ── native keyboard ───────────────────────────────────────────────────────
    const handleNativeKeyDown = (e) => {
        if (e.key === 'Enter') { e.preventDefault(); handleSubmit(); return; }
        if (!/^[a-z]$/i.test(e.key) && e.key !== 'Backspace') e.preventDefault();
    };

    const { converted, pending } = getHiraganaDisplay(input);

    // ────────────────────────────────────────────────────────────────────────
    // START SCREEN
    // ────────────────────────────────────────────────────────────────────────
    if (phase === "start") {
        return (
            <section className="h-[100dvh] flex flex-col items-center justify-center gap-6 bg-[#fffaf0] pt-[60px] px-6 font-jp">
                <h1 className="sr-only">{t.title}</h1>
                {IS_MOBILE_DEVICE && (
                <div className="flex border-2 border-[#b5435f] rounded-full overflow-hidden">
                    {[
                        { mode: 'desktop', icon: 'fa-keyboard',      label: t.desktop },
                        { mode: 'mobile',  icon: 'fa-mobile-screen', label: t.mobile  },
                    ].map(({ mode, icon, label }) => (
                        <button key={mode}
                            onClick={() => { playTap(); setKbMode(mode); }}
                            className={`px-5 py-2 text-sm font-semibold transition flex items-center gap-2
                                ${kbMode === mode
                                    ? "bg-[#b5435f] text-white"
                                    : "text-[#b5435f] hover:bg-[#b5435f]/5"}`}>
                            <i className={`fa-solid ${icon}`} />
                            {label}
                        </button>
                    ))}
                </div>
                )}

                <img src={typingGif} alt="typing" className="w-36 h-36 object-contain" />
                <p className="text-lg font-semibold text-gray-700 text-center max-w-xs">
                    {kbMode === "mobile" ? t.titleMobile : t.title}
                </p>

                {VOCAB_ITEMS.length === 0
                    ? <p className="text-sm text-red-400 text-center">{t.noAudio}</p>
                    : (
                        <motion.button whileTap={{ scale: 0.95 }}
                            onClick={startQuiz}
                            className="px-10 py-3 rounded-2xl bg-[#b5435f] text-white font-bold text-base
                                       hover:bg-[#9e3a52] transition shadow-md">
                            {t.start}
                        </motion.button>
                    )
                }
            </section>
        );
    }

    // ────────────────────────────────────────────────────────────────────────
    // QUIZ SCREEN
    // ────────────────────────────────────────────────────────────────────────
    if (phase === "quiz" && currentQ) {
        return (
            <section
                className="flex flex-col overflow-hidden bg-[#fffaf0] font-jp"
                style={{ ...(Object.keys(vpStyle).length ? vpStyle : { height: "100dvh" }), paddingTop: "60px", zIndex: 30 }}
            >
                {/* Progress bar */}
                <div className="flex-shrink-0 h-1 bg-gray-100">
                    <motion.div className="h-full bg-[#2797c4]"
                        animate={{ width: `${(qIdx / 10) * 100}%` }}
                        transition={{ duration: 0.35 }} />
                </div>

                <div className="flex-1 min-h-0 flex flex-col items-center justify-evenly px-5 py-2 gap-2 relative">

                    {/* Header bar */}
                    <div className="w-full max-w-md flex items-center justify-between flex-shrink-0">
                        <button
                            onClick={() => { playTap(); stopCountdown(); setPhase("start"); }}
                            className="text-gray-400 hover:text-gray-600 transition"
                            style={{ fontSize: "clamp(1rem,4.5vmin,1.25rem)" }}>
                            <i className="fa-solid fa-right-from-bracket"
                               style={{ display: "inline-block", transform: "scaleX(-1)" }} />
                        </button>

                        {/* Desktop / Mobile toggle — mobile devices only */}
                        {IS_MOBILE_DEVICE ? (
                        <div className="flex border border-[#b5435f] rounded-full overflow-hidden text-xs">
                            {[
                                { mode: 'desktop', icon: 'fa-keyboard' },
                                { mode: 'mobile',  icon: 'fa-mobile-screen' },
                            ].map(({ mode, icon }) => (
                                <button key={mode}
                                    onClick={() => { playTap(); setKbMode(mode); setFlickState(null); }}
                                    className={`px-3 py-1 transition flex items-center gap-1 font-semibold
                                        ${kbMode === mode ? "bg-[#b5435f] text-white"
                                        : "text-[#b5435f] hover:bg-[#b5435f]/5"}`}>
                                    <i className={`fa-solid ${icon}`} />
                                </button>
                            ))}
                        </div>
                        ) : <div />}

                        <div className="flex items-center gap-2">
                            {timeLeft !== null && !feedback && (
                                <CountdownRing key={qIdx} timeLeft={timeLeft} total={TOTAL_SECS} />
                            )}
                            <span className="text-sm font-bold text-[#b5435f]">
                                {qIdx + 1}<span className="font-normal text-gray-400"> / 10</span>
                            </span>
                        </div>
                    </div>

                    {/* Input / diff display */}
                    <div className="flex-shrink-0 min-h-[64px] flex items-center justify-center relative">
                        {feedback === "wrong" && wrongTyped !== undefined ? (
                            /* Show what was typed with gap at mismatch, all in gray */
                            <DiffDisplay correct={currentQ.hiragana} typed={wrongTyped} />
                        ) : kbMode === "mobile" ? (
                            input
                                ? <span className="text-[clamp(2rem,9vmin,3.5rem)] font-bold tracking-widest leading-none text-[#2797c4] font-jp">{input}</span>
                                : <BlinkingCursor />
                        ) : (
                            converted || pending
                                ? <span className="text-[clamp(2rem,9vmin,3.5rem)] font-bold tracking-widest leading-none font-jp">
                                    <span className="text-[#2797c4]">{converted}</span>
                                    <span className="text-gray-300">{pending}</span>
                                  </span>
                                : <BlinkingCursor />
                        )}
                        {/* ○/✕ overlaid ON the typed text */}
                        <AnimatePresence>
                            {feedback && (
                                <motion.div
                                    className="absolute inset-0 flex items-center justify-center pointer-events-none"
                                    initial={{ opacity: 0, scale: 0.3 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    exit={{ opacity: 0, scale: 0.3 }}
                                    transition={{ type: "spring", stiffness: 300, damping: 18 }}>
                                    <span className={`text-[clamp(2.5rem,10vmin,4.5rem)] font-bold leading-none drop-shadow-sm
                                        ${feedback === "correct" ? "text-green-500" : "text-red-500"}`}>
                                        {feedback === "correct" ? "○" : "✕"}
                                    </span>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>

                    {/* Headphone replay — or correct answer when wrong — or image when correct */}
                    <div className="flex-shrink-0 flex flex-col items-center gap-1">
                        {feedback === "wrong" ? (
                            <span className="text-2xl font-bold text-[#b5435f] font-jp tracking-wide py-2">
                                {currentQ.hiragana}
                            </span>
                        ) : feedback === "correct" ? (
                            (() => {
                                const card   = VOCAB_BY_JP[currentQ.hiragana];
                                const frames = getFramesForResult(currentQ, card);
                                return frames.length > 0
                                    ? <AnimatedVocabImg frames={frames} alt={currentQ.hiragana}
                                          className="w-36 h-36 object-cover" />
                                    : null;
                            })()
                        ) : (
                            <>
                                <button
                                    onClick={() => { playClick(); playQuestion(currentQ); }}
                                    className="w-14 h-14 rounded-full border-2 border-[#b5435f] bg-white
                                               flex items-center justify-center hover:bg-[#b5435f]/5 transition
                                               shadow-sm text-[#b5435f] text-xl">
                                    <i className="fa-solid fa-headphones" />
                                </button>
                                <span className="text-[10px] text-gray-400">{t.tapToListen}</span>
                            </>
                        )}
                    </div>

                    {/* Desktop mode — plain text input (triggers system keyboard) */}
                    {kbMode === "desktop" && (
                        <div className="w-full max-w-md flex gap-1.5 flex-shrink-0">
                            <input
                                ref={inputRef}
                                type="text"
                                inputMode="url"
                                lang="en"
                                autoComplete="off"
                                autoCorrect="off"
                                autoCapitalize="none"
                                spellCheck={false}
                                value={input}
                                onBeforeInput={(e) => {
                                    // Block any character that isn't a-z before it's inserted
                                    if (e.data && !/^[a-zA-Z]$/.test(e.data)) e.preventDefault();
                                }}
                                onCompositionStart={(e) => {
                                    // Reset native value immediately to abort IME composition display
                                    e.target.value = input;
                                }}
                                onCompositionEnd={(e) => {
                                    e.target.value = input; // discard composed text
                                    setInput(input);
                                }}
                                onChange={(e) => {
                                    if (isLocked || answeredRef.current) return;
                                    const val = e.target.value.replace(/[^a-zA-Z]/g, '').toLowerCase();
                                    setInput(val);
                                }}
                                onKeyDown={handleNativeKeyDown}
                                placeholder="romaji..."
                                className="flex-1 min-w-0 border-2 border-[#fdcb6e] rounded-xl px-3 py-2 text-base
                                           font-semibold focus:outline-none focus:border-[#b5435f] bg-white
                                           text-[#2797c4] font-jp disabled:opacity-50"
                                disabled={isLocked}
                            />
                            <motion.button
                                whileTap={{ scale: 0.95 }}
                                onClick={handleSubmit}
                                disabled={isLocked}
                                className="px-4 py-2 rounded-xl bg-[#b5435f] text-white font-bold text-sm
                                           hover:bg-[#9e3a52] transition shadow-sm disabled:opacity-40 whitespace-nowrap flex-shrink-0">
                                ENTER
                            </motion.button>
                            <motion.button
                                whileTap={{ scale: 0.95 }}
                                onClick={handlePass}
                                disabled={isLocked}
                                className="px-3 py-2 rounded-xl bg-gray-100 text-gray-500 font-bold
                                           hover:bg-gray-200 transition shadow-sm disabled:opacity-40 flex-shrink-0"
                                style={{ fontSize: "clamp(10px, 3vw, 14px)", whiteSpace: "nowrap" }}>
                                {t.pass}
                            </motion.button>
                        </div>
                    )}

                    {/* Progress dots */}
                    <div className="flex gap-1.5 items-center flex-shrink-0">
                        {Array.from({ length: 10 }).map((_, i) => {
                            const res = results[i];
                            return (
                                <div key={i} className={`rounded-full transition-all duration-300
                                    ${i === qIdx   ? "w-3 h-3 bg-[#b5435f]"
                                    : res?.correct ? "w-2 h-2 bg-green-500"
                                    : res          ? "w-2 h-2 bg-red-400"
                                    :                "w-2 h-2 bg-gray-200"}`} />
                            );
                        })}
                    </div>
                </div>

                {/* Mobile keyboard — Japanese flick input */}
                {kbMode === "mobile" && (() => {
                    const grid = KANA3_BASE;
                    const FLICK_THRESHOLD = 14;

                    const handleDakuten = () => {
                        setInput(prev => {
                            const chars = [...prev];
                            if (!chars.length) return prev;
                            const last = chars[chars.length - 1];
                            const mapped = SMALL_MAP[last] ?? DAKUTEN_MAP[last];
                            if (!mapped) return prev;
                            return chars.slice(0, -1).join('') + mapped;
                        });
                        setFlickState(null);
                    };
                    const BTN_H = 46; // px height for each key row
                    const GAP   = 4;  // px gap between keys

                    const getFlickDir = (dx, dy) => {
                        if (Math.abs(dx) < FLICK_THRESHOLD && Math.abs(dy) < FLICK_THRESHOLD) return null;
                        return Math.abs(dy) >= Math.abs(dx)
                            ? (dy < 0 ? 'u' : 'd')
                            : (dx > 0 ? 'r' : 'l');
                    };

                    const commitFlick = (cell, dir) => {
                        if (!cell || isLocked || answeredRef.current) return;
                        const kana = dir === null ? cell.c
                                   : dir === 'u'  ? cell.u
                                   : dir === 'd'  ? cell.d
                                   : dir === 'l'  ? cell.l
                                   :                cell.r;
                        if (kana) setInput(v => v + kana);
                    };

                    const makeTouchHandlers = (cell) => ({
                        onTouchStart: (e) => {
                            if (isLocked || answeredRef.current) return;
                            e.preventDefault();
                            const t = e.touches[0];
                            const rect = e.currentTarget.getBoundingClientRect();
                            flickTouchRef.current = { startX: t.clientX, startY: t.clientY, cell };
                            setFlickState({ cellKey: cell.key, dir: null, rect });
                        },
                        onTouchMove: (e) => {
                            if (!flickTouchRef.current) return;
                            e.preventDefault();
                            const t = e.touches[0];
                            const dx = t.clientX - flickTouchRef.current.startX;
                            const dy = t.clientY - flickTouchRef.current.startY;
                            setFlickState(prev => prev ? { ...prev, dir: getFlickDir(dx, dy) } : null);
                        },
                        onTouchEnd: (e) => {
                            if (!flickTouchRef.current) return;
                            e.preventDefault();
                            commitFlick(flickTouchRef.current.cell, flickState?.dir ?? null);
                            flickTouchRef.current = null;
                            setFlickState(null);
                        },
                        onTouchCancel: () => { flickTouchRef.current = null; setFlickState(null); },
                    });

                    // Flick popup cross overlay
                    const FlickPopup = () => {
                        if (!flickState) return null;
                        const allCells = [
                            ...KANA3_BASE.flat(), ...KANA3_VOICED.flat(),
                            N_CELL, WA_CELL, PUNCT_CELL,
                        ].filter(Boolean);
                        const cell = allCells.find(c => c.key === flickState.cellKey);
                        if (!cell) return null;
                        const { rect, dir } = flickState;
                        const cx = rect.left + rect.width / 2;
                        const cy = rect.top + rect.height / 2;
                        const S = 44;
                        const opts = [
                            { d: null, x: cx,     y: cy,     kana: cell.c },
                            { d: 'u',  x: cx,     y: cy - S, kana: cell.u },
                            { d: 'd',  x: cx,     y: cy + S, kana: cell.d },
                            { d: 'l',  x: cx - S, y: cy,     kana: cell.l },
                            { d: 'r',  x: cx + S, y: cy,     kana: cell.r },
                        ].filter(o => o.kana);
                        return (
                            <div className="fixed inset-0 pointer-events-none z-50">
                                {opts.map(o => (
                                    <div key={o.d ?? 'c'}
                                        style={{ position:'absolute', left: o.x-S/2, top: o.y-S/2, width:S, height:S }}
                                        className={`flex items-center justify-center rounded-xl text-lg font-bold shadow-md border font-jp
                                            ${dir === o.d
                                                ? 'bg-[#b5435f] text-white border-[#b5435f]'
                                                : o.d === null
                                                    ? 'bg-[#fdcb6e] text-gray-800 border-[#e0b84e]'
                                                    : 'bg-white text-gray-700 border-gray-300'}`}>
                                        {o.kana}
                                    </div>
                                ))}
                            </div>
                        );
                    };

                    // ── Helpers ────────────────────────────────────────────
                    const flickCls = (cellKey) =>
                        `rounded-xl shadow-sm text-xl font-bold font-jp disabled:opacity-40 border transition-colors
                         ${flickState?.cellKey === cellKey
                             ? 'bg-[#fdcb6e] border-[#e0b84e] text-gray-800'
                             : 'bg-white border-gray-300 text-gray-800 active:bg-gray-100'}`;

                    const funcCls = 'rounded-xl shadow-sm text-sm font-bold border bg-[#b4b0a8] border-gray-400 text-gray-700 active:bg-gray-400 disabled:opacity-40';

                    const renderFlick = (cell, key) => cell
                        ? <button key={key} disabled={isLocked} {...makeTouchHandlers(cell)}
                              className={flickCls(cell.key)} style={{ height: BTN_H }}>
                              {cell.key}
                          </button>
                        : <div key={key} style={{ height: BTN_H }} />;

                    // Build per-column arrays (5 rows each)
                    // Rows: kana-r0, kana-r1, kana-r2, ん-row, bottom-row
                    const col2 = [grid[0][0], grid[1][0], grid[2][0]]; // あたま / がばぁ
                    const col3 = [grid[0][1], grid[1][1], grid[2][1]]; // かなや / ざぱゃ
                    const col4 = [grid[0][2], grid[1][2], grid[2][2]]; // さはら / だnull null

                    return (
                        <>
                        <FlickPopup />
                        {/*
                          5-column layout (flex-col per column):

                          left  col2   col3   col4   right
                          [   ] [ あ ] [ か ] [ さ ] [ ⌫  ]
                          [   ] [ た ] [ な ] [ は ] [    ]  ← blank
                          [   ] [ ま ] [ や ] [ ら ] [    ]  ↑
                          [   ] [   ] [ ん ] [   ] [ENTER]  ← ENTER spans rows 3+4
                          [skip] [小゛゜] [わ] [、。] [   ]
                        */}
                        <div className="flex-shrink-0 w-full bg-[#d9d3c8] border-t border-gray-300 px-1 pt-1 pb-1 select-none"
                             style={{ touchAction: 'none' }}>
                            <div className="flex gap-1">

                                {/* ── Left column: 3 spacers + skip ── */}
                                <div className="flex flex-col gap-1" style={{ width: BTN_H }}>
                                    {[0,1,2].map(i => <div key={i} style={{ height: BTN_H }} />)}
                                    <button disabled={isLocked}
                                        onTouchStart={(e) => { e.preventDefault(); if (!isLocked) handlePass(); }}
                                        onClick={handlePass}
                                        className={funcCls}
                                        style={{ height: BTN_H, fontSize: '11px', whiteSpace: 'nowrap' }}>
                                        {t.pass}
                                    </button>
                                </div>

                                {/* ── Col 2: あたま 小゛゜ ── */}
                                <div className="flex flex-col gap-1 flex-1">
                                    {col2.map((cell, i) => renderFlick(cell, `c2_${i}`))}
                                    <button
                                        onTouchEnd={(e) => { e.preventDefault(); handleDakuten(); }}
                                        onClick={handleDakuten}
                                        className="rounded-xl shadow-sm text-sm font-bold border transition-colors bg-[#b4b0a8] text-gray-700 border-gray-400 active:bg-gray-400"
                                        style={{ height: BTN_H }}>
                                        小<br/>゛゜
                                    </button>
                                </div>

                                {/* ── Col 3: かなや わ ── */}
                                <div className="flex flex-col gap-1 flex-1">
                                    {col3.map((cell, i) => renderFlick(cell, `c3_${i}`))}
                                    {renderFlick(WA_CELL, 'wa')}
                                </div>

                                {/* ── Col 4: さはら 、。 ── */}
                                <div className="flex flex-col gap-1 flex-1">
                                    {col4.map((cell, i) => renderFlick(cell, `c4_${i}`))}
                                    {renderFlick(PUNCT_CELL, 'punct')}
                                </div>

                                {/* ── Right column: ⌫ blank ENTER(×2) ── */}
                                <div className="flex flex-col gap-1" style={{ width: BTN_H }}>
                                    <button
                                        onTouchStart={(e) => { e.preventDefault(); if (!isLocked) setInput(v => v.slice(0,-1)); }}
                                        onClick={() => { if (!isLocked) setInput(v => v.slice(0,-1)); }}
                                        disabled={isLocked}
                                        className={funcCls} style={{ height: BTN_H }}>
                                        ⌫
                                    </button>
                                    <div className="rounded-xl bg-[#b4b0a8] border border-gray-400"
                                         style={{ height: BTN_H }} />
                                    <button
                                        onTouchStart={(e) => { e.preventDefault(); if (!isLocked) handleSubmit(); }}
                                        onClick={handleSubmit}
                                        disabled={isLocked}
                                        className="rounded-xl bg-[#b5435f] text-white font-bold text-sm shadow-sm border border-[#9e3a52] active:bg-[#7d2d47] disabled:opacity-40"
                                        style={{ height: BTN_H * 2 + GAP }}>
                                        ENTER
                                    </button>
                                </div>

                            </div>
                        </div>
                        </>
                    );
                })()}
            </section>
        );
    }

    // ────────────────────────────────────────────────────────────────────────
    // RESULT SCREEN
    // ────────────────────────────────────────────────────────────────────────
    if (phase === "result") {
        const finalScore  = score;
        const wrongResults = results.filter(r => !r.correct);
        // All words where user made at least one wrong attempt (including retried-and-correct)
        const allWrongAttempts = [...wrongAttemptsRef.current.values()];
        // For each, check if they eventually got it right
        const finalResultMap = new Map(results.map(r => [r.q.hiragana, r.correct]));
        const msg = finalScore === 10 ? t.perfect
            : finalScore >= 7 ? t.great
            : finalScore >= 4 ? t.keepGoing
            : t.tryAgain;

        return (
            <>
            <motion.section
                className="min-h-[100dvh] flex flex-col items-center overflow-y-auto bg-[#fffaf0]
                           pt-[60px] px-5 py-8 gap-6 font-jp"
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }}>

                {/* Score */}
                <div className="text-center">
                    <p className="text-5xl font-bold text-[#b5435f]">
                        {finalScore}
                        <span className="text-2xl text-gray-400 font-normal"> {t.outOf}</span>
                    </p>
                    <p className="text-sm text-gray-500 mt-1">{msg}</p>
                    {totalTime !== null && (
                        <p className="text-sm text-[#2797c4] mt-2 font-semibold">
                            {totalTime}びょうでこたえたよ！
                        </p>
                    )}
                </div>

                {/* Wrong questions — all words with at least one wrong attempt */}
                {allWrongAttempts.length > 0 && (
                    <div className="w-full max-w-sm">
                        <p className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-3">{t.missed}</p>
                        <div className="flex flex-col gap-3">
                            {allWrongAttempts.map((r, i) => {
                                const eventuallyCorrect = finalResultMap.get(r.q.hiragana) === true;
                                const card   = VOCAB_BY_JP[r.q.hiragana];
                                const frames = getFramesForResult(r.q, card);
                                return (
                                <button key={i}
                                    onClick={() => { playClick(); playQuestion(r.q); }}
                                    className={`rounded-2xl shadow-sm px-3 py-3 flex items-center gap-3 w-full text-left active:scale-[0.98] transition-transform
                                        ${eventuallyCorrect ? "bg-green-50 border border-green-200" : "bg-white"}`}>

                                    {/* Left: animated image or headphone circle */}
                                    {frames.length > 0
                                        ? <AnimatedVocabImg frames={frames} alt={r.q.hiragana}
                                               className="w-14 h-14 object-cover rounded-xl flex-shrink-0" />
                                        : <div className={`flex-shrink-0 w-9 h-9 rounded-full border-2 flex items-center justify-center text-sm
                                                ${eventuallyCorrect ? "border-green-400 text-green-400" : "border-[#b5435f] text-[#b5435f]"}`}>
                                              <i className="fa-solid fa-headphones" />
                                          </div>
                                    }

                                    <div className="min-w-0 flex-1">
                                        {/* Romaji in blue above answer */}
                                        {card?.romaji && (
                                            <span className="text-xs text-[#2797c4] font-en block leading-tight mb-0.5">{card.romaji}</span>
                                        )}
                                        {/* Correct answer */}
                                        <div className="flex items-center gap-1 flex-wrap">
                                            <span className={`text-xl font-bold tracking-wide font-jp
                                                ${eventuallyCorrect ? "text-green-500" : "text-[#b5435f]"}`}>
                                                {r.q.hiragana}
                                            </span>
                                            {eventuallyCorrect && (
                                                <span className="text-xs text-green-400 ml-1">✓</span>
                                            )}
                                            {[...r.q.hiragana].some(c => TYPING_HINT_CHARS.has(c)) && (
                                                <span className="text-xs text-gray-400 font-en">
                                                    ({hiraganaToTypingRomaji(r.q.hiragana)})
                                                </span>
                                            )}
                                        </div>
                                        {/* EN + ZH below answer, horizontal */}
                                        {(card?.en || card?.zh) && (
                                            <div className="flex items-center gap-2 flex-wrap mt-0.5">
                                                {card?.en && <span className="text-xs text-gray-400 font-en">{card.en}</span>}
                                                {card?.zh && <span className="text-xs text-gray-400 font-zh">{card.zh}</span>}
                                            </div>
                                        )}
                                        {/* What user typed — all attempts */}
                                        {r.attempts.length === 1 && r.attempts[0] === "" ? (
                                            <span className="text-xs text-gray-400 italic mt-0.5">{t.skipped}</span>
                                        ) : (
                                            <div className="flex flex-col gap-0.5 mt-0.5">
                                                {r.attempts.map((typed, ai) => (
                                                    <div key={ai} className="flex items-center gap-1">
                                                        <span className="text-xs text-gray-400">{t.youTyped}</span>
                                                        <span className="text-base font-semibold tracking-wide font-jp">
                                                            {[...r.q.hiragana].map((c, ci) => {
                                                                const tc = [...typed][ci] ?? "";
                                                                const match = c === tc;
                                                                return (
                                                                    <span key={ci} className={match ? "text-green-400" : "text-red-500 font-bold"}>
                                                                        {tc || "＿"}
                                                                    </span>
                                                                );
                                                            })}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                    <i className={`fa-solid fa-headphones opacity-40 flex-shrink-0 text-sm
                                        ${eventuallyCorrect ? "text-green-400" : "text-[#b5435f]"}`} />
                                </button>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* Hanamaru — perfect 10, shown above play again */}
                {finalScore === 10 && resultRevealed && HANAMARU_VIDEO && (
                    <div className="pointer-events-none flex justify-center">
                        <HanamaruCanvas videoSrc={HANAMARU_VIDEO} />
                    </div>
                )}

                {/* Buttons */}
                <div className="flex flex-col gap-3 w-full max-w-xs">
                    <motion.button whileTap={{ scale: 0.96 }}
                        onClick={startQuiz}
                        className="w-full py-3 rounded-2xl bg-[#b5435f] text-white font-bold text-sm
                                   hover:bg-[#9e3a52] transition shadow-md">
                        {t.playAgain}
                    </motion.button>
                    <button
                        onClick={() => { playTap(); setPhase("start"); }}
                        className="text-xs text-gray-400 hover:text-gray-600 transition text-center">
                        {t.backToMenu}
                    </button>
                </div>
            </motion.section>

            {/* Good job overlay */}
            <AnimatePresence>
                {!resultRevealed && (
                    <motion.section
                        className="fixed inset-0 flex flex-col items-center justify-center gap-4 bg-[#fffaf0] z-50 font-jp"
                        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                        transition={{ duration: 0.4 }}>
                        <motion.p
                            className="text-[clamp(2rem,10vmin,4rem)] font-bold text-[#b5435f] text-center px-8 leading-tight"
                            initial={{ scale: 0.4, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            transition={{ type: "spring", stiffness: 180, damping: 14, delay: 0.1 }}>
                            {t.goodjob}
                        </motion.p>
                        <motion.div className="flex gap-1.5"
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.5, duration: 0.4 }}>
                            {Array.from({ length: 10 }).map((_, i) => (
                                <div key={i} className={`w-2 h-2 rounded-full
                                    ${results[i]?.correct ? "bg-green-500"
                                    : results[i]          ? "bg-red-400"
                                    :                       "bg-gray-200"}`} />
                            ))}
                        </motion.div>
                    </motion.section>
                )}
            </AnimatePresence>

            </>
        );
    }

}
